/**
 * U1a — login tenant resolution.
 *
 * Login is tenant-scoped on the backend: `POST /api/v1/auth/login` resolves a
 * tenant by `tenant_slug` (app/auth/service.py::resolve_tenant) and then looks
 * the user up WITHIN that tenant. The portal previously hardcoded
 * `tenant_slug:"default"`, so ONLY users in the `default` tenant could ever
 * log in — every seple-dev / boi-demo / seple user 401'd regardless of a
 * correct password.
 *
 * Resolution policy (DECISIONS LOCKED, U1a):
 *   - Primary signal: the host. `<slug>.sview360.seple.in` → `<slug>`.
 *   - The bare/default portal host `app.sview360.seple.in` (and the apex
 *     `sview360.seple.in`, and reserved labels) → `"default"` (backward-compat
 *     with the existing super-admin + default-tenant logins).
 *   - localhost / 127.* / *.local / preview hosts (railway/vercel/netlify/
 *     ngrok) → `"default"` (don't break local dev or ephemeral deploys).
 *   - Email-domain assist is a LAST-RESORT hint that fires ONLY for an
 *     unrecognized host — never for the default/apex/portal-subdomain/preview
 *     hosts above (so it can never override the backward-compat default). It
 *     derives a candidate generically from the email's domain label; it is NOT
 *     an enumerable tenant list (ISO/BFSI: no tenant enumeration). The backend
 *     still authoritatively validates the slug and 401s on any mismatch, so a
 *     wrong hint leaks nothing.
 *
 * This module is pure (host + email in, slug out) so it is unit-testable
 * without a DOM — see tenantSlug.test.ts.
 */

export const DEFAULT_TENANT_SLUG = "default";

/** LEGACY localStorage key. It once held the last successful login's slug,
 *  read back as a fallback ("Bug 2"). It is no longer read or written; login()
 *  removes it on every attempt. Kept only so old browsers get cleaned. */
export const LAST_TENANT_SLUG_KEY = "sview.lastTenantSlug";

/** The production portal apex. Tenant portals live at `<slug>.<PORTAL_DOMAIN>`. */
export const PORTAL_DOMAIN = "sview360.seple.in";

/**
 * Subdomain labels that are NOT tenant slugs — they map to the default tenant.
 * `app` is the bare/default portal host; `api` is the backend; `www` is the
 * marketing alias.
 */
const RESERVED_LABELS = new Set(["app", "www", "api", "portal"]);

/** Slug charset — lowercase alphanumerics + hyphen. Shape guard only (never a
 *  membership/enumeration check). */
const SLUG_PATTERN = /^[a-z0-9-]+$/;

/** Host suffixes for ephemeral/preview deploys that must resolve to default. */
const PREVIEW_SUFFIXES = [
  ".railway.app",
  ".up.railway.app",
  ".vercel.app",
  ".netlify.app",
  ".ngrok.io",
  ".ngrok-free.app",
];

/** True for local-dev hosts that must never break (always → default tenant). */
function isLocalHost(host: string): boolean {
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host === "[::1]" ||
    host.endsWith(".local") ||
    host.endsWith(".localhost")
  );
}

/** True for ephemeral preview hosts that must resolve to default. */
function isPreviewHost(host: string): boolean {
  return PREVIEW_SUFFIXES.some((s) => host.endsWith(s));
}

/**
 * Last-resort hint from the email domain's leading label. Generic (no curated
 * map). Returns null when no usable label can be derived. Intentionally
 * conservative: a multi-label public mailbox (e.g. gmail.com) yields its label
 * `gmail`, which the backend will simply reject — no enumeration, no leak.
 */
function tenantHintFromEmail(email: string | undefined): string | null {
  if (!email) return null;
  const at = email.lastIndexOf("@");
  if (at < 0) return null;
  const domain = email.slice(at + 1).toLowerCase().trim();
  if (!domain || domain.includes("..")) return null;
  const label = domain.split(".")[0];
  if (!label || RESERVED_LABELS.has(label)) return null;
  // Slug charset guard — slugs are lowercase alnum + hyphen.
  return SLUG_PATTERN.test(label) ? label : null;
}

/**
 * Resolve the tenant slug to send on login from the browser host, an explicit
 * opt-in `?tenant=` override, and (as a last-resort hint) the email entered.
 *
 * Precedence: explicit override → host → email hint → "default".
 *
 * @param host     Typically `window.location.host` (may include a port).
 * @param email    Optional — the email in the login form, used only as a
 *                 fallback hint for unrecognized hosts.
 * @param explicit Optional — an explicit slug from `?tenant=<slug>`. Honored
 *                 ONLY when present and slug-shaped. This is an opt-in escape
 *                 hatch for the bare host (app.sview360.seple.in) before
 *                 per-tenant wildcard DNS exists: the operator must already
 *                 KNOW the slug and type it into the URL — it is never listed,
 *                 suggested, or enumerated, and the backend still authoritatively
 *                 validates it (401 on mismatch). Preserves the no-enumeration
 *                 design (ISO/BFSI).
 */
export function resolveTenantSlug(
  host: string | undefined | null,
  email?: string,
  explicit?: string | null,
): string {
  // Highest precedence: an explicit, opt-in slug. Normalized + shape-guarded
  // only (never checked against a tenant list).
  const ex = (explicit || "").toLowerCase().trim();
  if (ex && SLUG_PATTERN.test(ex)) {
    return ex;
  }

  const h = (host || "").toLowerCase().split(":")[0].trim(); // strip any :port

  if (!h || isLocalHost(h) || isPreviewHost(h)) {
    return DEFAULT_TENANT_SLUG;
  }

  // Apex itself → default. The server resolves the real tenant from the email.
  if (h === PORTAL_DOMAIN) {
    return DEFAULT_TENANT_SLUG;
  }

  // `<slug>.sview360.seple.in` → `<slug>` (unless reserved/multi-label).
  const suffix = "." + PORTAL_DOMAIN;
  if (h.endsWith(suffix)) {
    const label = h.slice(0, h.length - suffix.length);
    if (!label || label.includes(".") || RESERVED_LABELS.has(label)) {
      // Bare portal host (app/www/api/portal.<domain>) → default; the server
      // resolves the real tenant from the email. A real subdomain still wins.
      return DEFAULT_TENANT_SLUG;
    }
    return label;
  }

  // Unrecognized host — only here may the email-domain assist apply.
  return tenantHintFromEmail(email) ?? DEFAULT_TENANT_SLUG;
}
